import React, {
    useEffect,
    useState,
} from 'react'
import { Link, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import {
    Container,
    Card,
    CardBody,
    CardHeader,
    Button,
    Col,
    Row,
    CustomInput,
    ButtonGroup,
    Progress,
} from 'reactstrap'

import type { Device } from '../../../types/domain'
import { readSingleDevice } from '../helpers'
import OverlayLoader from '../../../components/shared/overlay-loader'
import { Certificates } from '../parts/certificates'
import DeviceMonitoredState from './monitored-state'
import {
    alarmsActive as alarmsActiveCheck,
    isServiceInProgress,
    parseDeviceData,
    DeviceDataItem,
    DeviceEntity,
} from './parse-device-data'
import Modal from '../../../components/shared/modal'
import EditValue from './edit-value'
import ServiceHistoryForm from '../forms/service-history-form'
import DetailGoogleMap from './DetailGoogleMap'
import ButtonIcon from '../../../components/common/ButtonIcon'
import ModalMediaContent from './ModalMediaContent'
import DeviceOilLevelHistory from './oil-level-history'
import {
    ACTIVE_STATUS,
    ACTIVE_STATUS_TRIGGER_ADDRESS,
    ACTIVE_TIME_MODE,
    PASSAGE_COUNTER,
    REM_HIGHER_DOSAGE,
    REM_LOWER_DOSAGE,
    TIME_SENSOR_MODE_TRIGGER_ADDRESS,
} from '../../../components/shared/device-addresses'
import {
    refreshDeviceStatus,
    toggleTriggerDevice,
} from './helpers'
import Loader from '../../../components/shared/loader'
import DevicePumpPlanning from './device-pump-planning'
import DevicePumpTiming from './device-pump-timing'
import { database } from '../../../components/shared/database'
import {
    setDevicesData,
    setDeviceWriterDataAction,
} from '../../../reducers/actions/devices'
import { EditDevice } from '../forms/edit-device'
import useSearchState from '../../../components/shared/use-search-state'

interface RootState {
    devices: {
        deviceWriter: string[]
    }
}

interface BacklinkState {
    link?: string
}

type ModalName = 'alarmsModal' | 'problemReportingModal' | 'othersModal'

const isResponsive = window.innerWidth < 700

/**
 * Device detail page — shows device status, controls, oil level chart,
 * alarms and settings.
 */
const DeviceDetail = () => {
    const { deviceUid } = useParams<{ deviceUid: string }>()

    const [dataAlarms, setDataAlarms] = useState<DeviceDataItem[]>([])
    const [dataSettings, setDataSettings] = useState<DeviceDataItem[]>([])
    const [dataOthers, setDataOthers] = useState<DeviceDataItem[]>([])
    const [alarmsActive, setAlarmsActive] = useState(false)
    const [alarmsModal, setAlarmsModal] = useState(false)
    const [othersModal, setOthersModal] = useState(false)
    const [editDataModal, setEditDataModal] = useState<Record<string, unknown> | null>(null)
    const [problemReportingModal, setProblemReportingModal] = useState(false)
    const [deviceServiceMode, setDeviceServiceMode] = useState(false)
    const [deviceActiveStatus, setDeviceActiveStatus] = useState('0')
    const [devicePassageCounter, setDevicePassageCounter] = useState<string | null>(null)
    const [deviceIsTimeMode, setDeviceIsTimeMode] = useState<string | undefined>(undefined)
    const [deviceLowerDosageActive, setDeviceLowerDosageActive] = useState<string | undefined>(undefined)
    const [deviceHigherDosageActive, setDeviceHigherDosageActive] = useState<string | undefined>(undefined)
    const [device, setDevice] = useState<DeviceEntity | null>(null)

    const deviceWriter = useSelector((state: RootState) => state.devices.deviceWriter)

    const [backlink] = useSearchState<BacklinkState | null>(null, 'backlink')

    const { t } = useTranslation()

    useEffect(() => {
        const deviceWriterDb = database('device_writer')
            .where('deviceUid', '==', deviceUid)
            .onSnapshot((querySnapshot: { forEach: (cb: (doc: { data: () => unknown }) => void) => void }) => {
                const result: { dbAddress: string }[] = []
                querySnapshot.forEach((document: { data: () => unknown }) => {
                    result.push(document.data() as { dbAddress: string })
                })

                setDeviceWriterDataAction(result)
            })

        return () => deviceWriterDb()
    }, [deviceUid])

    useEffect(() => {
        const deviceDb = database('device')
            .doc(deviceUid as string)
            .onSnapshot((querySnapshot: { data: () => Record<string, unknown> | undefined }) => {
                const result = querySnapshot.data()
                if (!result) {
                    return
                }

                const deviceData = setDevicesData([result as unknown as Device], false)

                setDevice((deviceData?.[0] as DeviceEntity) || null)
            })

        return () => deviceDb()
    }, [deviceUid])

    useEffect(() => {
        setDeviceData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device])

    /**
     * Set device data into state
     */
    const setDeviceData = (): void => {
        const {
            dataAlarms: dataAlarmsArray,
            dataSettings: dataSettingsArray,
            dataOthers: dataOthersArray,
        } = parseDeviceData(device)

        const readData = device?.readData

        setDataAlarms(dataAlarmsArray)
        setDataSettings(dataSettingsArray)
        setDataOthers(dataOthersArray)
        setAlarmsActive(alarmsActiveCheck(device))
        setDeviceServiceMode(isServiceInProgress(device))
        setDeviceActiveStatus(readData?.find?.(deviceData => deviceData.hint === ACTIVE_STATUS)?.data ?? '0')
        setDevicePassageCounter(readData?.find?.(deviceData => deviceData.hint === PASSAGE_COUNTER)?.data ?? null)
        setDeviceIsTimeMode(readData?.find?.(deviceData => deviceData.hint === ACTIVE_TIME_MODE)?.data)
        setDeviceLowerDosageActive(readData?.find?.(deviceData => deviceData.hint === REM_LOWER_DOSAGE)?.data)
        setDeviceHigherDosageActive(readData?.find?.(deviceData => deviceData.hint === REM_HIGHER_DOSAGE)?.data)
    }

    /**
     * Show / hide modal
     *
     * @param modalName - name of the modal to toggle
     */
    const toggleModal = (modalName: ModalName): void => {
        if (modalName === 'alarmsModal') {
            setAlarmsModal(!alarmsModal)
        }

        if (modalName === 'problemReportingModal') {
            setProblemReportingModal(!problemReportingModal)
        }

        if (modalName === 'othersModal') {
            setOthersModal(!othersModal)
        }
    }

    if (!device) {
        return <OverlayLoader />
    }

    const oilDeviceActive = parseInt(deviceActiveStatus, 10) === 0

    return (
        <Container className="mb-5 mt-3">
            <Card>
                <CardHeader>
                    <Link to={backlink?.link || '/devices'}>
                        <i className="fas fa-arrow-left mr-2 text-dark" />
                    </Link>
                    {device.name}
                </CardHeader>
                <CardBody className="py-0">
                    <Row>
                        <DetailGoogleMap device={device as Parameters<typeof DetailGoogleMap>[0]['device']} />
                    </Row>
                </CardBody>
                <CardHeader className="bg-100 pl-4">
                    <Row className="align-items-center">
                        <Col lg={3}>
                            <h5>{device.name}</h5>
                            <span>{device.ipAddress}</span>
                        </Col>
                        <Col lg={9}>
                            <Button
                                onClick={() => refreshDeviceStatus(device as { uid: string; ipAddress: string; isMonitored: boolean })}
                                color="falcon-default"
                                size="sm"
                                className="mx-2 float-right"
                            >
                                {device.statusUpdating && (
                                    <Loader height={16} />
                                )}
                                {!device.statusUpdating && (
                                    <i className="fas fa-sync-alt mr-2" />
                                )}
                                {t('deviceData.refresh')}
                            </Button>
                            <Button
                                href={`/devices/${device.uid}/history`}
                                color="falcon-default"
                                size="sm"
                                className="mx-2 float-right"
                            >
                                <i className="fas fa-external-link-alt mr-2" />
                                {t('deviceServiceHistory.history')}
                            </Button>
                            <Button
                                transform="shrink-3 down-2"
                                color={alarmsActive ? 'danger' : 'success'}
                                size="sm"
                                className="mx-2 float-right"
                                onClick={() => toggleModal('alarmsModal')}
                            >
                                <i className="fas fa-exclamation-triangle mr-2" />
                                {t('deviceData.alarms')}
                            </Button>
                            <Button
                                onClick={() => toggleModal('problemReportingModal')}
                                transform="shrink-3 down-2"
                                color="warning"
                                size="sm"
                                className="mx-2 float-right"
                            >
                                <i className="fas fa-exclamation-triangle mr-2" />
                                {t('device.problemReporting')}
                            </Button>
                            <EditDevice
                                updatedDevice={device}
                                onModalClose={() => readSingleDevice(deviceUid as string)}
                            >
                                <Button
                                    color="light"
                                    size="sm"
                                    transform="shrink-3 down-2"
                                    className="mx-2 float-right"
                                >
                                    <i className="fas fa-pencil-alt text-500" />
                                </Button>
                            </EditDevice>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody>
                    <Row className="pl-4 pb-5">
                        <Col lg={6} className="mt-n4">
                            <Row className="pt-3">
                                <Col lg={12} className="pl-0">
                                    <ModalMediaContent
                                        title={t('deviceDetail.overview')}
                                        icon="fas fa-info-circle"
                                    >
                                        <Row>
                                            {device.isMonitored && (
                                                <Col lg={4}>
                                                    <div className="d-flex align-items-center">
                                                        <div className="d-flex align-items-center mr-2">
                                                            <label className="switch mt-1">
                                                                {deviceWriter.some(
                                                                    deviceWriterData =>
                                                                        deviceWriterData ===
                                                                        ACTIVE_STATUS_TRIGGER_ADDRESS,
                                                                ) ? (
                                                                        <Loader
                                                                            width="16"
                                                                            className="pl-1"
                                                                        />
                                                                    ) : (
                                                                        <>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={parseInt(deviceActiveStatus, 10) === 0}
                                                                                onChange={() => toggleTriggerDevice(
                                                                                    device as { uid: string; ipAddress: string; isMonitored: boolean },
                                                                                    ACTIVE_STATUS_TRIGGER_ADDRESS,
                                                                                )}
                                                                            />
                                                                            <div className="slider" />
                                                                        </>
                                                                    )}
                                                            </label>
                                                        </div>
                                                        <span>
                                                            {oilDeviceActive
                                                                ? t('deviceDetail.oil.active')
                                                                : t('deviceDetail.oil.notActive')
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <i
                                                            className={classNames(
                                                                {
                                                                    'fas mr-3 ml-1': true,
                                                                    'fa-check text-success': device?.lastSeenStatus === 'ok',
                                                                    'fa-times text-gray-700 ': device?.lastSeenStatus !== 'ok',
                                                                },
                                                            )}
                                                        />
                                                        {device?.lastSeenStatus === 'ok'
                                                            ? t('device.active')
                                                            : t('device.notActive')}
                                                    </div>
                                                </Col>
                                            )}
                                            <Col lg={8}>
                                                {device.isMonitored && (
                                                    <>
                                                        <div className="d-flex align-items-center">
                                                            <i
                                                                className={classNames(
                                                                    {
                                                                        'fas mr-3 ml-1': true,
                                                                        'fa-tools text-info': deviceServiceMode,
                                                                        'fa-circle text-success': !deviceServiceMode,
                                                                    },
                                                                )}
                                                            />
                                                            {t('deviceDetail.serviceMode')}
                                                        </div>
                                                    </>
                                                )}
                                                <div className="d-flex align-items-center mt-1">
                                                    <i className={classNames(
                                                        {
                                                            'fas mr-3 ml-1': true,
                                                            'fa-check text-success': device.isMonitored,
                                                            'fa-times text-gray-700 ': !device.isMonitored,
                                                        },
                                                    )}
                                                    />
                                                    {device.isMonitored
                                                        ? t('device.monitored')
                                                        : t('device.notMonitored')}
                                                </div>
                                            </Col>
                                        </Row>
                                    </ModalMediaContent>
                                </Col>
                            </Row>
                            {isResponsive && (
                                <Row className="pt-3">
                                    <Col lg={12} className="p-0 m-0">
                                        {device.isMonitored && (
                                            <ModalMediaContent
                                                title={t('deviceDetail.basicInfo')}
                                                icon="fas fa-info-circle"
                                            >
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex flex-row align-items-center">
                                                        <span className="font-weight-bold mb-1 mr-2">
                                                            {t('deviceDetail.oilLevel')}:
                                                        </span>
                                                        {Math.floor(device.oilLevelPercentage ?? 0)}%
                                                        <Progress
                                                            value={Math.floor(device.oilLevelPercentage ?? 0)}
                                                            className="w-50 ml-2"
                                                        >
                                                            {Math.floor(device.oilLevelPercentage ?? 0)}%
                                                        </Progress>
                                                    </div>
                                                    <div>
                                                        <span className="font-weight-bold mr-2">
                                                            {t('deviceDetail.voltage')}:
                                                        </span>
                                                        {device.voltage}V
                                                    </div>
                                                </div>
                                            </ModalMediaContent>
                                        )}
                                    </Col>
                                </Row>
                            )}
                            <Row className="pt-3 pr-5">
                                <Col lg={12} className="p-0">
                                    {device.isMonitored && (
                                        <ModalMediaContent
                                            title={t('deviceDetail.modeSettings')}
                                            icon="fas fa-sliders-h"
                                        >
                                            <ButtonGroup className="btn-group-toggle">
                                                {deviceWriter.some(
                                                    deviceWriterData =>
                                                        deviceWriterData ===
                                                        TIME_SENSOR_MODE_TRIGGER_ADDRESS,
                                                ) ? (
                                                        <Loader />
                                                    ) : (
                                                        <>
                                                            <Button
                                                                color="theme-default"
                                                                className={classNames(
                                                                    'custom-radio-success p-0 text-left custom-control custom-radio mr-2',
                                                                    {
                                                                        active: deviceIsTimeMode === '1',
                                                                    },
                                                                )}
                                                            >
                                                                <CustomInput
                                                                    type="radio"
                                                                    id="timeMode"
                                                                    label={t('deviceDetail.timeMode')}
                                                                    checked={parseInt(deviceIsTimeMode ?? '0', 10) === 1}
                                                                    onClick={() => toggleTriggerDevice(
                                                                        device as { uid: string; ipAddress: string; isMonitored: boolean },
                                                                        TIME_SENSOR_MODE_TRIGGER_ADDRESS,
                                                                    )}
                                                                />
                                                            </Button>
                                                            <Button
                                                                color="theme-default"
                                                                className={classNames(
                                                                    'custom-radio-success p-0 text-left custom-control custom-radio mr-2',
                                                                    {
                                                                        active: deviceIsTimeMode === '0',
                                                                    },
                                                                )}
                                                            >
                                                                <CustomInput
                                                                    type="radio"
                                                                    id="sensorMode"
                                                                    label={t('deviceDetail.sensorMode')}
                                                                    checked={parseInt(deviceIsTimeMode ?? '0', 10) === 0}
                                                                    onClick={() => toggleTriggerDevice(
                                                                        device as { uid: string; ipAddress: string; isMonitored: boolean },
                                                                        TIME_SENSOR_MODE_TRIGGER_ADDRESS,
                                                                    )}
                                                                />
                                                            </Button>
                                                        </>
                                                    )}
                                            </ButtonGroup>
                                            <Row className="mt-3 pt-3">
                                                <Col lg={12}>
                                                    <span className="font-weight-bold">
                                                        {t('pumpTiming')}
                                                    </span>
                                                    <DevicePumpTiming
                                                        dataSettings={dataSettings}
                                                        className="my-1 d-flex flex-row align-items-center"
                                                        deviceWriter={deviceWriter}
                                                        device={device}
                                                        deviceLowerDosageActive={deviceLowerDosageActive}
                                                        deviceHigherDosageActive={deviceHigherDosageActive}
                                                        deviceIsTimeMode={deviceIsTimeMode}
                                                    />
                                                </Col>
                                                <Col lg={12}>
                                                    <span className="font-weight-bold">
                                                        {t('pumpPlanning')}
                                                    </span>
                                                    <DevicePumpPlanning
                                                        dataSettings={dataSettings}
                                                        className="my-1 d-flex flex-row align-items-center"
                                                        deviceWriter={deviceWriter}
                                                        device={device}
                                                    />
                                                </Col>
                                            </Row>
                                        </ModalMediaContent>
                                    )}
                                </Col>
                            </Row>
                            {isResponsive && (
                                <Row className="pt-3">
                                    <Col lg={12} className="pl-0">
                                        <ModalMediaContent
                                            title={t('deviceDetail.passages')}
                                            icon="fas fa-info-circle"
                                        >
                                            <div>
                                                <span className="font-weight-bold mr-1">
                                                    {t('deviceData.hint.PASSAGE_COUNTER')}:
                                                </span>
                                                {devicePassageCounter}
                                            </div>
                                        </ModalMediaContent>
                                    </Col>
                                </Row>
                            )}
                            <Row className="pt-3">
                                <ModalMediaContent
                                    title={t('deviceDetail.settings')}
                                    icon="fas fa-tag"
                                    className="mb-3"
                                >
                                    <ButtonIcon
                                        icon="check"
                                        transform="shrink-3 down-2"
                                        color="falcon-default"
                                        size="sm"
                                        className="mr-2"
                                        target="_blank"
                                        href={`http://${device.ipAddress}`}
                                    >
                                        {t('deviceData.router')}
                                    </ButtonIcon>
                                    <ButtonIcon
                                        icon="check"
                                        transform="shrink-3 down-2"
                                        color="falcon-default"
                                        size="sm"
                                        target="_blank"
                                        href={`http://${device.ipAddress}:${device?.logoWebPort || '8080'}`}
                                    >
                                        {t('deviceData.logo')}
                                    </ButtonIcon>
                                </ModalMediaContent>
                            </Row>
                            <Row className="pt-3">
                                <ModalMediaContent
                                    title={t('deviceDetail.certificates')}
                                    icon="fas fa-file-download"
                                >
                                    <Certificates device={device} />
                                </ModalMediaContent>
                            </Row>
                        </Col>
                        <Col lg={6} className="mt-n4">
                            {!isResponsive && (
                                <Row className="pt-3">
                                    <Col lg={12} className="p-0 m-0">
                                        {device.isMonitored && (
                                            <ModalMediaContent
                                                title={t('deviceDetail.basicInfo')}
                                                icon="fas fa-info-circle"
                                            >
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex flex-row align-items-center">
                                                        <span className="font-weight-bold mb-1 mr-2">
                                                            {t('deviceDetail.oilLevel')}:
                                                        </span>
                                                        {Math.floor(device.oilLevelPercentage ?? 0)}%
                                                        <Progress
                                                            value={Math.floor(device.oilLevelPercentage ?? 0)}
                                                            className="w-50 ml-2"
                                                        >
                                                            {Math.floor(device.oilLevelPercentage ?? 0)}%
                                                        </Progress>
                                                    </div>
                                                    <div>
                                                        <span className="font-weight-bold mr-2">
                                                            {t('deviceDetail.voltage')}:
                                                        </span>
                                                        {device.voltage}V
                                                    </div>
                                                </div>
                                            </ModalMediaContent>
                                        )}
                                    </Col>
                                </Row>
                            )}
                            {device.isMonitored && (
                                <>
                                    <Row className="pt-3">
                                        <Col lg={12} className="pl-0">
                                            <ModalMediaContent
                                                title={t('deviceDetail.oilLevelGraph')}
                                                icon="fas fa-fill-drip"
                                            >
                                                <DeviceOilLevelHistory
                                                    device={device}
                                                />
                                            </ModalMediaContent>
                                        </Col>
                                    </Row>
                                    {!isResponsive && (
                                        <Row className="pt-3">
                                            <Col lg={12} className="pl-0">
                                                <ModalMediaContent
                                                    title={t('deviceDetail.passages')}
                                                    icon="fas fa-info-circle"
                                                >
                                                    <div>
                                                        <span className="font-weight-bold mr-1">
                                                            {t('deviceData.hint.PASSAGE_COUNTER')}:
                                                        </span>
                                                        {devicePassageCounter}
                                                    </div>
                                                </ModalMediaContent>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row className="pt-3">
                                        <ModalMediaContent
                                            title="Info"
                                            icon="fas fa-info-circle"
                                        >
                                            <div>
                                                <span className="font-weight-bold">
                                                    {t('deviceData.lastSeen')}:
                                                </span>
                                                <span className="ml-2">
                                                    {window.moment
                                                        .utc(device.lastSeen)
                                                        .local()
                                                        .format('llll')}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-weight-bold">
                                                    {t('deviceData.lastLatency')}:
                                                </span>
                                                <span className="ml-2">
                                                    {device.latencyMs}
                                                </span>
                                            </div>
                                        </ModalMediaContent>
                                    </Row>
                                </>
                            )}

                            {device.description &&
                                device.description !== '' && (
                                <Row className="pt-3 pr-4">
                                    <ModalMediaContent
                                        title={t('deviceDetail.description')}
                                        icon="fas fa-align-left"
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: device.description,
                                            }}
                                        />
                                    </ModalMediaContent>
                                </Row>
                            )}
                        </Col>
                    </Row>

                    {alarmsModal && (
                        <Modal
                            description={t('deviceData.alarms')}
                            handleHideModal={() => toggleModal('alarmsModal')}
                            content={() => (
                                <div className="p-3">
                                    {dataAlarms.map?.((deviceData, key) => (
                                        <DeviceMonitoredState
                                            device={device}
                                            deviceData={deviceData}
                                            deviceWriter={deviceWriter}
                                            key={key}
                                        />
                                    ))}
                                </div>
                            )}
                        />
                    )}

                    {othersModal && (
                        <Modal
                            description={t('deviceData.otherParams')}
                            handleHideModal={() => toggleModal('othersModal')}
                            content={() => (
                                <div className="p-3">
                                    {dataOthers.map?.((deviceData, key) => (
                                        <DeviceMonitoredState
                                            device={device}
                                            deviceData={deviceData}
                                            deviceWriter={deviceWriter}
                                            key={key}
                                        />
                                    ))}
                                </div>
                            )}
                        />
                    )}

                    {editDataModal && (
                        <Modal
                            description={t('deviceDetail.edit')}
                            handleHideModal={() => setEditDataModal(null)}
                            content={() => (
                                <EditValue
                                    editDataModal={editDataModal as { device: { uid: string; ipAddress: string }; deviceData: { address: string; hint?: string; data: string; type?: string } }}
                                    onSuccess={() => setEditDataModal(null)}
                                />
                            )}
                        />
                    )}

                    {problemReportingModal && (
                        <Modal
                            description={t('device.problemReporting')}
                            handleHideModal={() => toggleModal('problemReportingModal')}
                            dialogClass="modal-lg modal-tall"
                            content={() => (
                                <div className="p-3">
                                    <ServiceHistoryForm
                                        onSuccess={() => toggleModal('problemReportingModal')}
                                        hideShowAll
                                        device={device as { uid: string }}
                                        reportType="problemReporting"
                                    />
                                </div>
                            )}
                        />
                    )}
                </CardBody>
            </Card>
        </Container>
    )
}

export default DeviceDetail
